import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  List,
  ListItem,
  Divider,
  Box,
} from "@material-ui/core";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";
import PrintIcon from "@material-ui/icons/Print";
import { QRCodeSVG } from "qrcode.react";
import { renderToStaticMarkup } from "react-dom/server";
import { useAuthState } from "../../context/auth";
import { listQrCodes } from "../../api/qrCodes";
import QrScannerDialog from "./QrScannerDialog";
import logoUrl from "../../assets/images/logo.svg";

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  url: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    wordBreak: "break-all",
    textAlign: "center",
    maxWidth: 420,
  },
  pairings: {
    width: "100%",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  details: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(1),
    maxWidth: 520,
  },
  pairingItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  pairingLabel: {
    fontWeight: 600,
  },
  pairingCode: {
    color: theme.palette.text.secondary,
    fontFamily: "Menlo, Monaco, Consolas, monospace",
    fontSize: "0.78rem",
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
    wordBreak: "break-all",
  },
  mutedText: {
    color: theme.palette.text.secondary,
  },
  section: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  secondaryAction: {
    marginTop: theme.spacing(2),
  },
}));

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const QrCodeDialog = ({ open, onClose, fieldId, fieldName, farmName }) => {
  const classes = useStyles();
  const url = `https://web.agri-tech.co.uk/fields/${fieldId}`;
  const { token, client } = useAuthState();
  const [pairings, setPairings] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const hasPairings = pairings.length > 0;
  const pairingCountLabel =
    pairings.length === 1
      ? "1 custom code paired"
      : `${pairings.length} custom codes paired`;

  const loadPairings = useCallback(async () => {
    if (!token || !client?.id) return;
    try {
      const res = await listQrCodes(token, client.id);
      setPairings(res.data.filter((qr) => qr.field_id === fieldId));
    } catch {
      // non-critical in preview modal
    }
  }, [token, client, fieldId]);

  useEffect(() => {
    if (open) {
      loadPairings();
    }
  }, [open, loadPairings]);

  const openPrintableView = () => {
    const safeFieldName = escapeHtml(fieldName);
    const safeFarmName = escapeHtml(farmName || "No farm set");
    const safeClientName = escapeHtml(client?.name || "Agri-tech");
    const printableLogoUrl = new URL(logoUrl, window.location.href).href;
    const safeLogoUrl = escapeHtml(printableLogoUrl);
    const qrMarkup = renderToStaticMarkup(
      <QRCodeSVG value={url} size={320} level="M" includeMargin />
    );
    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>QR Code - ${safeFieldName}</title>
          <style>
            * { box-sizing: border-box; }
            @page {
              size: A5 portrait;
              margin: 12mm;
            }
            body {
              margin: 0;
              font-family: Arial, sans-serif;
              background: #eef3f3;
              color: #132526;
            }
            .page {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 32px;
            }
            .sheet {
              width: 100%;
              max-width: 148mm;
              background: #ffffff;
              border: 1px solid #d7e0e0;
              box-shadow: 0 16px 40px rgba(19, 37, 38, 0.12);
              padding: 32px;
            }
            .brand {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 24px;
              padding-bottom: 24px;
              border-bottom: 2px solid #e4ecec;
              margin-bottom: 28px;
            }
            .brand img {
              width: 92px;
              height: auto;
            }
            .eyebrow {
              text-transform: uppercase;
              letter-spacing: 0.14em;
              font-size: 12px;
              color: #5d7375;
              margin-bottom: 8px;
            }
            h1 {
              margin: 0;
              font-size: 34px;
              line-height: 1.1;
            }
            .meta {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 16px;
              margin-bottom: 32px;
            }
            .meta-card {
              border: 1px solid #dde7e7;
              background: #f8fbfb;
              padding: 16px;
            }
            .meta-card span {
              display: block;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              color: #6e8587;
              margin-bottom: 6px;
            }
            .meta-card strong {
              font-size: 16px;
              line-height: 1.35;
            }
            .qr-wrap {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 18px;
              padding: 32px;
              border: 2px dashed #d6e2e2;
            }
            .actions {
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              margin-top: 28px;
            }
            .actions button {
              border: 0;
              padding: 12px 18px;
              font-size: 14px;
              cursor: pointer;
            }
            .actions .secondary {
              background: #edf3f3;
              color: #173234;
            }
            .actions .primary {
              background: #2f8f83;
              color: #fff;
            }
            @media print {
              body { background: #fff; }
              .page { padding: 0; }
              .sheet {
                box-shadow: none;
                border: 0;
                max-width: none;
              }
              .actions { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="sheet">
              <div class="brand">
                <div>
                  <div class="eyebrow">Agri-tech Field QR</div>
                  <h1>${safeFieldName}</h1>
                </div>
                <img src="${safeLogoUrl}" alt="Agri-tech logo" />
              </div>
              <div class="meta">
                <div class="meta-card">
                  <span>Client</span>
                  <strong>${safeClientName}</strong>
                </div>
                <div class="meta-card">
                  <span>Farm</span>
                  <strong>${safeFarmName}</strong>
                </div>
              </div>
              <div class="qr-wrap">
                <div>${qrMarkup}</div>
              </div>
              <div class="actions">
                <button class="secondary" onclick="window.close()">Close</button>
                <button class="primary" onclick="window.print()">Print</button>
              </div>
            </div>
          </div>
          <script>
            window.__QR_VALUE__ = ${JSON.stringify(url)};
          </script>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html" });
    const printUrl = URL.createObjectURL(blob);
    const printWindow = window.open(printUrl, "_blank");
    if (!printWindow) {
      URL.revokeObjectURL(printUrl);
      return;
    }
    window.setTimeout(() => URL.revokeObjectURL(printUrl), 60000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>QR Code — {fieldName}</DialogTitle>
      <DialogContent className={classes.content}>
        <QRCodeSVG value={url} size={256} level="M" includeMargin />
        <Typography variant="h6">{fieldName}</Typography>
        <div className={classes.details}>
          <Typography variant="body2" className={classes.mutedText}>
            {farmName || "No farm set"}
          </Typography>
          {hasPairings && (
            <>
              <Typography variant="body2" className={classes.mutedText}>
                •
              </Typography>
              <Typography variant="body2" className={classes.mutedText}>
                {pairingCountLabel}
              </Typography>
            </>
          )}
        </div>
        <div className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={openPrintableView}
          >
            Print
          </Button>
        </div>

        {hasPairings && (
          <>
            <Divider flexItem />

            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom>
                Custom QR Codes
              </Typography>
              <div className={classes.pairings}>
                <List dense disablePadding>
                  {pairings.map((qr) => (
                    <ListItem
                      key={qr.id}
                      divider
                      className={classes.pairingItem}
                    >
                      <Box width="100%">
                        {qr.label ? (
                          <>
                            <Typography
                              variant="body2"
                              className={classes.pairingLabel}
                            >
                              {qr.label}
                            </Typography>
                            <Typography className={classes.pairingCode}>
                              {qr.content}
                            </Typography>
                          </>
                        ) : (
                          <Typography className={classes.pairingCode}>
                            {qr.content}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </div>
            </Box>
          </>
        )}

        <div className={classes.secondaryAction}>
          <Button
            color="default"
            variant="text"
            size="small"
            startIcon={<CenterFocusStrongIcon />}
            onClick={() => setShowScanner(true)}
          >
            Pair QR Code
          </Button>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
      <QrScannerDialog
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onFieldFound={() => {}}
        fixedFieldId={fieldId}
        fixedFieldName={fieldName}
        onPairingChange={() => loadPairings()}
      />
    </Dialog>
  );
};

export default QrCodeDialog;
